import { sum } from 'lodash';

import { RectSize } from '../../../../../../../common/maths/geometry/rect/rectTypes';
import { TableColWidthsGU } from '../../../../../../../common/table/TableTypes';
import {
    TABLE_DEFAULT_COL_WIDTH,
    TABLE_DEFAULT_ROW_HEIGHT,
    TABLE_MIN_ROW_COUNT,
} from '../../../../../../../common/table/tableConstants';
import { getTableElementFromHotInstance } from '../../domUtils/hotTableDomAccessorUtils';
import { getMaxFromSelections } from '../../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';
import { lastColWithContent, lastRowWithContent } from './hotTableContentBoundsUtils';
import { MNHotInstance } from '../../../../bridge/tableHotInstanceTypes';

/**
 * Returns the rendered table height in grid units for the current zoom level.
 */
const getTotalTableHeightGU = (
    hotInstance: MNHotInstance,
    gridSize: number,
    getContextZoomScale: () => number,
): number => {
    const tableElement = getTableElementFromHotInstance(hotInstance);
    if (!tableElement) return 0;

    const tableElementHeight = tableElement.getBoundingClientRect().height;
    const tableHeightGU = tableElementHeight / gridSize;
    return tableHeightGU / getContextZoomScale();
};

/**
 * Calculates how many rows and columns should be added or removed for a drag-resize gesture.
 */
export const resizeTable = (
    hotInstance: MNHotInstance,
    colWidthsGU: TableColWidthsGU,
    tempSize: RectSize,
    gridSize: number,
    getContextZoomScale: () => number,
): { columnDif: number; rowDif: number } => {
    const { width: tempWidth, height: tempHeight } = tempSize;

    const tableWidth = sum(colWidthsGU);
    const widthDif = tempWidth - tableWidth;
    const tableHeight = getTotalTableHeightGU(hotInstance, gridSize, getContextZoomScale);
    const heightDif = tempHeight - tableHeight;

    const widthAdjustment = TABLE_DEFAULT_COL_WIDTH / 2;
    const heightAdjustment = TABLE_DEFAULT_ROW_HEIGHT / 2;

    let columnDif = Math.floor((widthDif + widthAdjustment) / TABLE_DEFAULT_COL_WIDTH);
    let rowDif = Math.floor((heightDif + heightAdjustment) / TABLE_DEFAULT_ROW_HEIGHT);

    if (columnDif < 0) {
        const finalColIndex = hotInstance.countCols() - 1;
        const proposedFinalIndex = finalColIndex + columnDif;
        const maxColWithContent = lastColWithContent(hotInstance) || 0;
        const maxSelectedCol = getMaxFromSelections(hotInstance.getSelected())?.col || 0;
        const maxColWithContentOrSelection = Math.max(maxColWithContent, maxSelectedCol);

        if (proposedFinalIndex < maxColWithContentOrSelection) {
            columnDif = maxColWithContentOrSelection - finalColIndex;
        }
    }

    if (rowDif < 0) {
        const finalRowIndex = hotInstance.countRows() - 1;
        const proposedFinalIndex = finalRowIndex + rowDif;
        const maxRowWithContent = lastRowWithContent(hotInstance) || 0;
        const maxSelectedRow = getMaxFromSelections(hotInstance.getSelected())?.row || 0;
        const maxRowWithContentOrSelection = Math.max(maxRowWithContent, maxSelectedRow);

        if (proposedFinalIndex < maxRowWithContentOrSelection) {
            rowDif = maxRowWithContentOrSelection - finalRowIndex;
        }

        const currentRowCount = hotInstance.countRows();
        if (currentRowCount + rowDif < TABLE_MIN_ROW_COUNT) {
            rowDif = TABLE_MIN_ROW_COUNT - currentRowCount;
        }
    }

    return { columnDif, rowDif };
};
